export type TextDirection = 'ltr' | 'rtl';

/** What a document is drawn with: `lang` only when a tool recorded the language, `dir` whenever it is known. */
export interface IDocumentTextDirection {
  readonly dir?: TextDirection;
  readonly lang?: string;
}

/** ISO 15924 codes of the right-to-left scripts a language's likely script can resolve to. */
const RTL_SCRIPT_CODES = ['Adlm', 'Arab', 'Hebr', 'Mand', 'Nkoo', 'Rohg', 'Samr', 'Syrc', 'Thaa'];
const RTL_SCRIPTS = new Set(RTL_SCRIPT_CODES);

/** Letters in the scripts above; digits and punctuation are neutral and count for neither side. */
const RTL_LETTER = new RegExp(`[${RTL_SCRIPT_CODES.map((code) => `\\p{Script=${code}}`).join('')}]`, 'u');
const LETTER = /\p{L}/gu;

/** Enough text to tell a document's language apart without scanning a long one on every keystroke. */
export const DIRECTION_SAMPLE_CHARS = 4000;

/** The share of letters the other direction needs before a recorded language counts as stale. */
const STALE_LANGUAGE_SHARE = 0.8;

/** Letters the sample needs before it can overrule a recorded language: a translation streams in a block at a time, often starting with a heading kept in the source language. */
const MIN_LETTERS_TO_DROP_LANGUAGE = 50;

/** The direction a BCP 47 tag is written in, from its explicit or likely script. An unparseable tag reads as LTR. */
export function directionOfLanguage(tag: string): TextDirection {
  try {
    const { script } = new Intl.Locale(tag).maximize();
    return script && RTL_SCRIPTS.has(script) ? 'rtl' : 'ltr';
  } catch {
    return 'ltr';
  }
}

type LetterCounts = Readonly<Record<TextDirection, number>>;

/** Letters in the leading sample, by the direction their script is written in. */
function countLetters(text: string): LetterCounts {
  const counts = { rtl: 0, ltr: 0 };
  for (const [letter] of text.slice(0, DIRECTION_SAMPLE_CHARS).matchAll(LETTER)) {
    counts[RTL_LETTER.test(letter) ? 'rtl' : 'ltr'] += 1;
  }
  return counts;
}

function majorityDirection({ rtl, ltr }: LetterCounts): TextDirection | undefined {
  if (rtl === 0 && ltr === 0) return undefined;
  return rtl > ltr ? 'rtl' : 'ltr';
}

/**
 * A recorded language decides both attributes unless at least {@link STALE_LANGUAGE_SHARE} of the sampled
 * letters run the other way, so a bilingual worksheet keeps its tag, as does a translation that keeps a
 * heading in the source language. Past that share, and with at least {@link MIN_LETTERS_TO_DROP_LANGUAGE}
 * letters sampled, the text was rewritten and the stale tag is dropped.
 * Without a tag only the direction can be read off the text: Farsi and Arabic share a script, so `lang`
 * stays unset.
 */
export function resolveTextDirection({ language, text }: { language: string | null | undefined; text: string }): IDocumentTextDirection {
  const counts = countLetters(text);
  const detected = majorityDirection(counts);
  if (!language) return detected ? { dir: detected } : {};
  const recorded = directionOfLanguage(language);
  const sampled = counts.rtl + counts.ltr;
  const rewritten =
    detected !== undefined &&
    detected !== recorded &&
    sampled >= MIN_LETTERS_TO_DROP_LANGUAGE &&
    counts[detected] >= STALE_LANGUAGE_SHARE * sampled;
  if (rewritten) return { dir: detected };
  return { dir: recorded, lang: language };
}
